import React, { useState, useEffect } from "react";
import { Formik, Field, Form } from "formik";
import {
    InputGroup,
    FieldWrappers,
    Label,
    SubmitBtn,
    SideCancelBtn,
    Wrapper,
    ScrollableContent,
    ActionSection,
} from "../../../Components/Styles/InputStyles";
import { MdClose, MdDelete } from "react-icons/md";
import tw from "tailwind-styled-components";
import * as Yup from "yup";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import { addDuesTypesForExtendStay } from "../../../Config";

const extendStaySchema = Yup.object().shape({
    extendDays: Yup.number()
        .min(1, "Must extend by at least 1 day")
        .max(29, "Cannot extend more than 29 days")
        .required("Number of days is required"),
});

const dueSchema = Yup.object().shape({
    totalDue: Yup.number()
        .min(1, "Minimum due amount is 1")
        .max(10000, "Maximum due amount is 10,000")
        .required("Due amount is required"),
    dueType: Yup.string()
        .required("Due type is required"),
});

const ExtendStay = ({ extendStayMutate, extendStayLoading, handleClose, selectedData }) => {
    const initialValues = {
        extendDays: "",
        tenantId: selectedData?.data?.id,
    };

    const [height, setHeight] = useState(window.innerHeight);
    const [showDueForm, setShowDueForm] = useState(false);
    const [dues, setDues] = useState([]);
    const [dueFormValues, setDueFormValues] = useState({
        totalDue: "",
        dueType: "",
    });
    const [dueFormErrors, setDueFormErrors] = useState({});

    useEffect(() => {
        const handleResize = () => {
            setHeight(window.innerHeight);
        };

        window.addEventListener('resize', handleResize);
        handleResize();

        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, []);

    const handleSubmit = (values) => {
        const submitData = {
            extendedDays: parseInt(values.extendDays),
            duesList: dues.length > 0 ? dues.map(due => ({
                amount: parseInt(due.amount),
                balance: parseInt(due.amount),
                type: parseInt(due.type)
            })) : [],
            tenantId: selectedData?.data?.id,
        };
        extendStayMutate(submitData);
    };

    const handleDueFormChange = (field, value) => {
        setDueFormValues(prev => ({
            ...prev,
            [field]: value
        }));

        if (dueFormErrors[field]) {
            setDueFormErrors(prev => ({
                ...prev,
                [field]: ""
            }));
        }
    };

    const handleAddDue = async () => {
        try {
            await dueSchema.validate(dueFormValues, { abortEarly: false });

            const newDue = {
                id: Date.now(),
                amount: parseInt(dueFormValues.totalDue),
                type: parseInt(dueFormValues.dueType),
                typeLabel: addDuesTypesForExtendStay.find(dt => dt.value === parseInt(dueFormValues.dueType))?.label || dueFormValues.dueType
            };

            setDues(prev => [...prev, newDue]);
            setDueFormValues({ totalDue: "", dueType: "" });
            setDueFormErrors({});
            setShowDueForm(false);
        } catch (error) {
            if (error instanceof Yup.ValidationError) {
                const errors = {};
                error.inner.forEach(err => {
                    errors[err.path] = err.message;
                });
                setDueFormErrors(errors);
            }
        }
    };

    const handleRemoveDue = (dueId) => {
        setDues(prev => prev.filter(due => due.id !== dueId));
    };

    const handleCancelDue = () => {
        setDueFormValues({ totalDue: "", dueType: "" });
        setDueFormErrors({});
        setShowDueForm(false);
    };

    return (
        <Container style={{ height: `${height}px`, overflow: 'auto', WebkitOverflowScrolling: 'touch' }}>
            <Header>
                <h1 className="text-white text-lg font-medium">Extend Stay</h1>
                <button onClick={handleClose}>
                    <MdClose color="white" size={24} />
                </button>
            </Header>
            <div className="px-6 h-full flex flex-col justify-between">
                <Formik
                    initialValues={initialValues}
                    onSubmit={handleSubmit}
                    validationSchema={extendStaySchema}
                    validateOnChange={true}
                    validateOnBlur={true}
                >
                    {({ values, setFieldValue, errors, touched }) => (
                        <Form>
                            <ScrollableContent className="max-h-[calc(100dvh-140px)]">
                                <Wrapper>
                                    {/* Extend Days Field */}
                                    <InputGroup>
                                        <Label htmlFor="extendDays">Extend Stay By (No. of Days) *</Label>
                                        <FieldWrappers>
                                            <Field
                                                type="number"
                                                name="extendDays"
                                                id="extendDays"
                                                autoComplete="off"
                                                min="1"
                                                max="29"
                                            />
                                        </FieldWrappers>
                                        {errors.extendDays && touched.extendDays && (
                                            <ErrorMessage>{errors.extendDays}</ErrorMessage>
                                        )}
                                    </InputGroup>

                                    <InputGroup>
                                        <Label>Dues (Optional)</Label>

                                        {!showDueForm && (
                                            <DueToggleButton
                                                type="button"
                                                onClick={() => setShowDueForm(true)}
                                            >
                                                Add Dues
                                            </DueToggleButton>
                                        )}

                                        {showDueForm && (
                                            <DueFormContainer>
                                                <InputGroup>
                                                    <Label htmlFor="totalDue">Total Due *</Label>
                                                    <FieldWrappers>
                                                        <Field
                                                            type="number"
                                                            name="totalDue"
                                                            id="totalDue"
                                                            value={dueFormValues.totalDue}
                                                            onChange={(e) => handleDueFormChange("totalDue", e.target.value)}
                                                            autoComplete="off"
                                                            min="1"
                                                            max="10000"
                                                            placeholder="Enter amount (1-10000)"
                                                        />
                                                    </FieldWrappers>
                                                    {dueFormErrors.totalDue && (
                                                        <ErrorMessage>{dueFormErrors.totalDue}</ErrorMessage>
                                                    )}
                                                </InputGroup>

                                                <InputGroup>
                                                    <Label htmlFor="dueType">Due Type *</Label>
                                                    <Select
                                                        name="dueType"
                                                        id="dueType"
                                                        className="w-full pl-2"
                                                        value={dueFormValues.dueType}
                                                        onChange={(e) => handleDueFormChange("dueType", e.target.value)}
                                                        style={{ height: "46px" }}
                                                        error={!!dueFormErrors.dueType}
                                                    >
                                                        <MenuItem value="">
                                                            <em>Select Due Type</em>
                                                        </MenuItem>
                                                        {addDuesTypesForExtendStay.map((type) => (
                                                            <MenuItem key={type.value} value={type.value}>
                                                                {type.label}
                                                            </MenuItem>
                                                        ))}
                                                    </Select>
                                                    {dueFormErrors.dueType && (
                                                        <ErrorMessage>{dueFormErrors.dueType}</ErrorMessage>
                                                    )}
                                                </InputGroup>

                                                <DueActionSection>
                                                    <DueCancelButton type="button" onClick={handleCancelDue}>
                                                        Cancel
                                                    </DueCancelButton>
                                                    <DueSaveButton
                                                        type="button"
                                                        onClick={handleAddDue}
                                                        disabled={!dueFormValues.totalDue || !dueFormValues.dueType}
                                                    >
                                                        Save Changes
                                                    </DueSaveButton>
                                                </DueActionSection>
                                            </DueFormContainer>
                                        )}

                                        {dues.length > 0 && (
                                            <DuesList>
                                                <Label>Added Dues:</Label>
                                                {dues.map((due) => (
                                                    <DueItem key={due.id}>
                                                        <DueInfo>
                                                            <DueAmount>₹{due.amount}</DueAmount>
                                                            <DueType>
                                                                {addDuesTypesForExtendStay.find(dt => dt.value === due.type)?.label || `Type ${due.type}`}
                                                            </DueType>
                                                        </DueInfo>
                                                        <RemoveDueButton
                                                            type="button"
                                                            onClick={() => handleRemoveDue(due.id)}
                                                        >
                                                            <MdDelete size={18} />
                                                        </RemoveDueButton>
                                                    </DueItem>
                                                ))}
                                            </DuesList>
                                        )}
                                    </InputGroup>
                                </Wrapper>
                            </ScrollableContent>
                            <ActionSection>
                                <SideCancelBtn type="button" onClick={handleClose}>
                                    Cancel
                                </SideCancelBtn>
                                <SubmitBtn type="submit" disabled={extendStayLoading}>
                                    {extendStayLoading ? "Loading..." : "Extend Stay"}
                                </SubmitBtn>
                            </ActionSection>
                        </Form>
                    )}
                </Formik>
            </div>
        </Container>
    );
};

// Styled Components
const Container = tw.div`bg-white w-full h-full flex flex-col relative`;
const Header = tw.div`flex items-center justify-between bg-[#26A69A] py-4 px-4`;
const ErrorMessage = tw.div`text-red-500 text-xs mt-1`;
const DueToggleButton = tw.button`w-full py-2 px-4 bg-gray-100 border border-gray-300 rounded-md text-gray-700 font-medium hover:bg-gray-200 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-opacity-50`;
const DueFormContainer = tw.div`border border-gray-300 rounded-md p-4 bg-gray-50 space-y-4`;
const DueActionSection = tw.div`flex gap-3 pt-2`;
const DueCancelButton = tw.button`flex-1 py-2 px-4 bg-gray-200 text-gray-700 rounded-md  hover:bg-gray-300 transition-colors font-medium`;
const DueSaveButton = tw.button`flex-1 py-2 px-4 bg-blue-600 text-white rounded-md  hover:bg-blue-700 transition-colors font-medium disabled:bg-gray-400 disabled:cursor-not-allowed`;
const DuesList = tw.div`mt-4 space-y-2`;
const DueItem = tw.div`flex items-center justify-between p-3 bg-white border border-gray-200 rounded-md shadow-sm`;
const DueInfo = tw.div`flex items-center gap-4`;
const DueAmount = tw.span`font-semibold text-gray-800`;
const DueType = tw.span`text-gray-600 text-sm`;
const RemoveDueButton = tw.button`p-1 text-red-500 hover:text-red-700 hover:bg-red-50 rounded transition-colors`;

export default ExtendStay;